/**
 * How a product page separates its sections from one another.
 *
 * WHY THIS IS A LIB. Exactly the reasoning in src/lib/product-gallery.ts: the
 * product page is rendered in THREE places that share no code, the real page and
 * the two hand-rolled previews inside the create and edit forms. A separator
 * rule written in one of them would be wrong in the other two within a release,
 * and a seller whose preview disagrees with their published page is the failure
 * this whole family of libs exists to prevent.
 *
 * WHY IT LIVES ON `details` AND NOT ON `design`, despite the control sitting in
 * the Design Preferences card. `product.design` is gated behind the "Customise
 * this product page" master switch, whose other settings are theme overrides
 * (starfield, accent, light/dark), and every one of them is ignored when that
 * switch is off. Section separators are structural, not a theme: a seller who
 * wants dividers should not have to also take over the buyer's light/dark
 * preference to get them. `details.frameWidthMode` and `details.coverInGallery`
 * are already structural page settings living here, so this sits beside them.
 */

/**
 * `none` is the DEFAULT and is exactly how the page has always looked: sections
 * separated by whitespace alone.
 * `dividers` draws a hairline above each section.
 * `outline` boxes every section and butts them together so their borders are
 * shared, which reads as one outline drawn around and between all of them.
 */
export type SectionSeparator = 'none' | 'dividers' | 'outline';

/**
 * What an unconfigured product uses, and what a new one starts on.
 *
 * `none` is both the read-side fallback for a document with no field on it and
 * the write-side start, deliberately: two constants would mean a product
 * published last year and one published today disagree about what "default"
 * means, and the seller's editor would show one thing while the live page
 * showed the other. It also means this setting changes NOTHING about any
 * existing page until a seller opts in.
 */
export const DEFAULT_SECTION_SEPARATOR: SectionSeparator = 'none';

/** The choices, in the order they are offered. Labels are seller-facing copy. */
export const SECTION_SEPARATORS: ReadonlyArray<{
  value: SectionSeparator;
  label: string;
  description: string;
}> = [
  {
    value: 'none',
    label: 'Nothing',
    description: 'Sections are separated by space alone. This is the default.',
  },
  {
    value: 'dividers',
    label: 'Dividers',
    description: 'A thin line between each section.',
  },
  {
    value: 'outline',
    label: 'Connected outline',
    description: 'Each section is boxed, sharing its edges with the sections above and below.',
  },
];

/**
 * The separator a product is set to.
 *
 * Validated against the closed list rather than trusted: the value reaches a
 * className, and `details` is client-written, so an unrecognised string is
 * either a broken layout or a way to smuggle utility classes onto someone
 * else's page depending on how carelessly it is used downstream.
 */
export function resolveSectionSeparator(
  details: { sectionSeparator?: unknown } | null | undefined,
): SectionSeparator {
  const stored = details?.sectionSeparator;
  return SECTION_SEPARATORS.some((s) => s.value === stored)
    ? (stored as SectionSeparator)
    : DEFAULT_SECTION_SEPARATOR;
}

/**
 * Classes for ONE content section (the description, the price, the spec table,
 * the reviews, and so on).
 *
 * `dividers` keeps the existing rhythm and adds a rule at the top of each
 * section, so the gap the layout already provides sits above the line and the
 * padding below it belongs to the section.
 *
 * `outline` boxes the section and pulls it up by a single pixel, so its top
 * border lands exactly on the previous section's bottom border and the two
 * share one line. That is what makes a stack of boxes read as one connected
 * outline rather than a column of separate cards, and it needs no knowledge of
 * which section happens to be first or last, which matters because almost every
 * one of them is conditional.
 */
export function sectionSurfaceClasses(separator: SectionSeparator): string {
  switch (separator) {
    case 'dividers':
      return 'border-t pt-6';
    case 'outline':
      return 'border p-4 -mt-px';
    case 'none':
    default:
      return '';
  }
}

/**
 * Whether a section's own inner Card should drop its border.
 *
 * The spec table and the review summary are already `Card`s. Inside an outlined
 * section that is a box drawn inside a box, which reads as a mistake, so in
 * that one mode the inner card goes flat and the section's own outline is the
 * only frame.
 */
export function flattensInnerCard(separator: SectionSeparator): boolean {
  return separator === 'outline';
}

/** Classes that flatten an inner Card, for use with the answer above. */
export const FLAT_CARD_CLASSES = 'border-0 bg-transparent shadow-none';

/**
 * Vertical spacing between sections, for the grid and for each column.
 *
 * `outline` collapses it to nothing, because boxes that do not touch cannot
 * share an edge. Everything else keeps the spacing the page has always had.
 *
 * The header blocks (the name, the seller, the short description, the gallery
 * frame) are NOT sections and carry their own spacing instead, via
 * `sectionHeaderClasses`; without that they would sit flush against each other
 * the moment the container's spacing went to zero.
 */
export function sectionLayoutClasses(separator: SectionSeparator): {
  grid: string;
  leftColumn: string;
  rightColumn: string;
} {
  if (separator === 'outline') {
    return { grid: 'gap-y-0', leftColumn: 'md:space-y-0', rightColumn: 'md:space-y-0' };
  }
  return { grid: 'gap-y-6', leftColumn: 'md:space-y-8', rightColumn: 'md:space-y-6' };
}

/**
 * Column spacing for the two EDITOR PREVIEWS.
 *
 * Same rule as `sectionLayoutClasses`, different breakpoint prefix. The live
 * page flattens its columns with `display: contents` below `md`, so its spacing
 * only applies from `md` up and the grid's own `gap-y` covers the phone; the
 * previews are plain two-column grids whose columns are real at every width, so
 * their spacing is unprefixed. Stating it here rather than inline in two
 * 3000-line forms is the whole reason this file exists.
 */
export function previewColumnClasses(separator: SectionSeparator): { left: string; right: string } {
  return separator === 'outline'
    ? { left: 'space-y-0', right: 'space-y-0' }
    : { left: 'space-y-8', right: 'space-y-6' };
}

/**
 * Classes for a section that sits OUTSIDE the two-column grid: the suggested
 * products row, which is a sibling below it.
 *
 * It needs its own rule because it has no container spacing to lean on. In
 * `none` that means keeping the generous break the row has always had; in the
 * other two it means supplying the gap the columns would otherwise have given
 * it, so the row lines up with the sections above rather than floating.
 */
export function trailingSectionClasses(separator: SectionSeparator): string {
  switch (separator) {
    case 'dividers':
      return 'mt-6 border-t pt-6';
    case 'outline':
      return 'border p-4 -mt-px';
    case 'none':
    default:
      return 'pt-12';
  }
}

/**
 * The product name, the seller and the short description.
 *
 * In `outline` these are sections like any other, so the outline runs around
 * and between them too and the page reads as one connected frame from the name
 * down. They used to be exempt, carrying a plain margin to make up for the
 * container spacing the mode collapses, which left the three of them floating
 * above a boxed stack.
 *
 * The GALLERY is not here even though it sits alongside them: it is already a
 * bordered frame, so it needs squaring rather than boxing. See galleryClasses.
 */
export function sectionHeaderClasses(separator: SectionSeparator): string {
  return separator === 'outline' ? sectionSurfaceClasses(separator) : '';
}

/**
 * The gallery's three pieces, which cannot take the ordinary section treatment
 * because the frame is ALREADY a bordered box.
 *
 * Wrapping it in another one would draw a border around a border, so in
 * `outline` the roles move out by one: the block that holds the frame and its
 * thumbnail strip becomes the section box, and the frame gives up its own
 * border and its rounded corners to become the picture inside it. The strip
 * then sits inside that box rather than orphaned below it, and gets the padding
 * the frame does not want (the image stays edge to edge, which is the point of
 * squaring the corners).
 *
 * Everything else keeps the rounded, self-bordered frame the gallery has always
 * had.
 */
export function galleryClasses(separator: SectionSeparator): {
  /** The block holding a STANDARD frame and its thumbnails. */
  block: string;
  /** A standard frame, which sits inside that block. */
  frame: string;
  /**
   * A FULL-WIDTH frame, which has no such block to sit in.
   *
   * It keeps its own border and only loses its corners. The full-width block is
   * a centred, max-width container whose thumbnails may be a rail beside the
   * picture rather than a strip beneath it, so it is not a section box and
   * cannot become one. Handing this frame the standard `frame` classes would
   * strip the only border it has and leave the gallery with no outline at all,
   * which is the exact opposite of what turning the mode on asks for.
   */
  fullWidthFrame: string;
  /** The thumbnail strip beneath a standard frame. */
  thumbnails: string;
} {
  if (separator !== 'outline') {
    return { block: '', frame: '', fullWidthFrame: '', thumbnails: '' };
  }
  return {
    block: 'border -mt-px',
    frame: 'border-0 rounded-none',
    fullWidthFrame: 'rounded-none',
    thumbnails: 'px-3 pb-3',
  };
}
